import { WebStorageStateStore } from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';

import { env } from '@/lib/env';

/**
 * Where the identity provider sends the browser back after signing in.
 *
 * **Deliberately not under `/auth`.** Keycloak is served on the same origin at
 * `/auth` (`KC_HTTP_RELATIVE_PATH`), and the gateway routes `PathPrefix(/auth)`
 * to it at priority 10 — so a callback at `/auth/callback` never reaches this
 * app at all. The browser arrives back carrying a valid authorization code and
 * Keycloak answers 404, which reads as a broken sign-in rather than as a routing
 * collision. Any path outside `/auth` and `/api` falls through to the SPA.
 */
export const CALLBACK_PATH = '/oidc/callback';

/**
 * An in-memory `Storage`, so the session does not outlive the tab.
 *
 * `oidc-client-ts` insists on something Storage-shaped; giving it a Map keeps the
 * user (and the tokens inside it) out of `sessionStorage` (FR-010).
 */
class MemoryStorage implements Storage {
  private readonly entries = new Map<string, string>();

  get length(): number {
    return this.entries.size;
  }
  clear(): void {
    this.entries.clear();
  }
  getItem(key: string): string | null {
    return this.entries.get(key) ?? null;
  }
  key(index: number): string | null {
    return [...this.entries.keys()][index] ?? null;
  }
  removeItem(key: string): void {
    this.entries.delete(key);
  }
  setItem(key: string, value: string): void {
    this.entries.set(key, value);
  }
}

export const oidcConfig: AuthProviderProps = {
  authority: env.VITE_OIDC_AUTHORITY,
  client_id: env.VITE_OIDC_CLIENT_ID,
  redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
  post_logout_redirect_uri: window.location.origin,
  // Authorization Code + PKCE. `oidc-client-ts` derives the challenge itself; the
  // realm requires S256 and refuses `plain`.
  response_type: 'code',
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: new MemoryStorage() }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // Renewal is driven from the request seam on a 401 rather than a background
  // timer, so a tab left open overnight renews when it is next used, not
  // repeatedly while nobody is looking.
  automaticSilentRenew: false,
};
